import { useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { trackLead } from "@/lib/pixels";

/**
 * Formulaire "liste d'attente" affiché quand aucune place n'est disponible
 * près de l'élève. On garde ses coordonnées pour le prévenir dès qu'une place
 * se libère dans sa ville.
 */
export default function WaitlistForm({ defaultCity = "", compact = false }) {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    phone: "",
    city: defaultCity,
    postal_code: "",
    radius_km: 25,
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.email || !form.city) {
      toast.error("Indique au moins ton email et ta ville.");
      return;
    }
    if (!form.postal_code || form.postal_code.trim().length < 4) {
      toast.error("Indique ton code postal pour qu'on te prévienne des auto-écoles proches.");
      return;
    }
    if (!consent) {
      toast.error("Coche la case pour être recontacté.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/public/booking/waitlist", {
        first_name: form.first_name || null,
        email: form.email,
        phone: form.phone || "",
        city: form.city,
        postal_code: form.postal_code || null,
        radius_km: form.radius_km,
        accepted_terms: consent,
      });
      // Google Ads : conversion « Envoi de formulaire de lead » (1 €) — pas pour une réinscription
      if (data?.id && !data?.already) trackLead(data.id, "waitlist");
      setDone(true);
      toast.success("C'est noté ! On te prévient dès qu'une auto-école ouvre près de chez toi.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="waitlist-done"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
        <p className="mt-3 text-base font-bold text-emerald-800">{"Tu es sur la liste d'attente !"}</p>
        <p className="mt-1 text-sm text-emerald-700">
          {`On te prévient par email dès qu'une auto-école ouvre des places en accéléré dans un rayon de ${form.radius_km} km autour de ${form.city || "chez toi"}.`}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="waitlist-form"
      className="rounded-2xl border border-amber-200 bg-amber-50/70 p-6"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-600">
          <BellRing className="h-5 w-5" />
        </span>
        <div>
          <p className="text-base font-bold text-ink">
            {"Tu ne trouves pas de ville proche ?"}
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            {"Laisse tes coordonnées et on te prévient dès qu'une auto-école ouvre des places en accéléré près de chez toi."}
          </p>
        </div>
      </div>

      <div className={`mt-4 grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input
          data-testid="wl-first"
          placeholder="Prénom (facultatif)"
          value={form.first_name}
          onChange={set("first_name")}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm"
        />
        <input
          data-testid="wl-email"
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={set("email")}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm"
        />
        <input
          data-testid="wl-phone"
          placeholder="Téléphone (facultatif)"
          value={form.phone}
          onChange={set("phone")}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm"
        />
        <input
          data-testid="wl-city"
          placeholder="Ta ville"
          value={form.city}
          onChange={set("city")}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm"
        />
        <input
          data-testid="wl-postal"
          placeholder="Code postal *"
          value={form.postal_code}
          onChange={set("postal_code")}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm"
        />
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold text-neutral-700">
          Préviens-moi si une auto-école ouvre des places en accéléré dans un rayon de :
        </p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="wl-radius">
          {[10, 25, 50, 100].map((km) => (
            <button
              key={km}
              type="button"
              onClick={() => setForm((f) => ({ ...f, radius_km: km }))}
              data-testid={`wl-radius-${km}`}
              className={`rounded-full px-3 py-1.5 text-sm font-bold transition ${
                form.radius_km === km
                  ? "bg-brand text-white"
                  : "bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-brand"
              }`}
            >
              {km} km
            </button>
          ))}
        </div>
      </div>

      <label className="mt-3 flex items-start gap-2 text-xs text-neutral-500">
        <input
          type="checkbox"
          data-testid="wl-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
        />
        <span>{"J'accepte d'être recontacté par PlanPermis dès qu'une auto-école ouvre près de chez moi (RGPD)."}</span>
      </label>

      <button
        onClick={submit}
        disabled={loading}
        data-testid="wl-submit"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 font-bold text-white transition hover:bg-brand/90 disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
        Me prévenir quand une place se libère
      </button>
    </div>
  );
}
